import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionDisputeCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  OpenTransactionDisputeRequestDto,
  TransactionDisputeDto,
} from "@/features/wallet/api/dto";

/**
 * Ouvre une contestation de transfert (`POST /TransactionDisputes/open`,
 * guide v3.1) — expéditeur uniquement, au plus tard 1 h après l'envoi
 * (`Disputes__OpeningWindowMinutes`), sur un transfert entre portefeuilles
 * COMPLETED (jamais un transfert par scan QR ; un transfert Non-Member se
 * traite par `Transactions/{id}/cancel`). Le montant est gelé chez le
 * bénéficiaire dans la limite de son disponible.
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, clé persistée par empreinte du
 * corps, scope par transaction) : rejouer la même tentative (reload, retry)
 * rejoue la réponse d'origine ; un motif différent produit une nouvelle
 * empreinte donc une nouvelle clé (nouvelle action distincte). 404 : transaction
 * inconnue ou appelant non expéditeur ; 422 : non contestable (la clé est
 * alors oubliée pour autoriser une nouvelle tentative).
 */
export const useOpenTransactionDispute = (transactionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactionDisputes", "open", transactionId],
    mutationFn: (body: OpenTransactionDisputeRequestDto) =>
      mutateIdempotent<TransactionDisputeDto>({
        scopeId: `wallet.transactionDisputes.open.${transactionId}`,
        url: walletEndpoints.transactionDisputes.open,
        body,
      }),
    onSuccess: () =>
      invalidateAfterMutation(
        queryClient,
        walletTransactionDisputeCacheKeys(undefined, transactionId),
        { resetListPage: true }
      ),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useOpenTransactionDispute failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
